import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Bot, Send, Plus, History, Lightbulb, GitCompare, ShieldCheck,
  HelpCircle, Loader2, ChevronLeft, Sparkles, Terminal
} from 'lucide-react';

export const MODES = [
  { id: 'general', label: 'General', icon: Sparkles, color: 'text-[#FF5E1A]', bg: 'bg-[#FF5E1A]/10' },
  { id: 'explain', label: 'Explain', icon: Lightbulb, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  { id: 'compare', label: 'Compare', icon: GitCompare, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  { id: 'advise', label: 'Advise', icon: HelpCircle, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  { id: 'validate', label: 'Validate', icon: ShieldCheck, color: 'text-red-400', bg: 'bg-red-400/10' },
];

export const QUICK_PROMPTS = [
  "I just uploaded my bin — what ECU is this and what maps did you find?",
  "Explain what LTFT clamping means and when it triggers",
  "Compare my stock vs my latest tuned binary — what changed?",
  "Is it safe to modify these fuel cells at high load?",
  "My AFR is reading lean at WOT — walk me through the fix",
  "What's a conservative first scaling multiplier for switching to E85?",
];

export function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.role}`}>
      <div className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 mt-1 ${isUser ? 'bg-zinc-800' : 'bg-[#FF5E1A]/20'}`}>
        {isUser ? <Terminal className="w-3.5 h-3.5 text-zinc-400" /> : <Bot className="w-3.5 h-3.5 text-[#FF5E1A]" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        {msg.mode && msg.mode !== 'general' && !isUser && (
          <Badge className={`mb-1.5 text-[9px] px-1.5 py-0 rounded-sm ${MODES.find(m => m.id === msg.mode)?.bg || 'bg-zinc-800'} ${MODES.find(m => m.id === msg.mode)?.color || 'text-zinc-400'}`}>
            {msg.mode.toUpperCase()}
          </Badge>
        )}
        <div className={`rounded-sm px-4 py-3 text-sm leading-relaxed ${isUser ? 'bg-[#FF5E1A]/10 border border-[#FF5E1A]/20 text-white' : 'bg-[#111111] border border-[#27272A] text-zinc-200'}`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none prose-headings:font-['Outfit'] prose-headings:text-white prose-headings:font-bold prose-code:text-[#FF5E1A] prose-code:bg-[#1A1A1A] prose-code:px-1 prose-code:py-0.5 prose-code:rounded-sm prose-code:text-xs prose-code:font-['JetBrains_Mono'] prose-pre:bg-[#0A0A0A] prose-pre:border prose-pre:border-[#27272A] prose-pre:rounded-sm prose-strong:text-white prose-em:text-zinc-300 prose-li:text-zinc-300 prose-p:text-zinc-300 prose-a:text-[#FF5E1A] prose-a:no-underline hover:prose-a:underline">
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </div>
          )}
        </div>
        {msg.created_at && (
          <p className="text-[10px] text-zinc-600 mt-1 font-['JetBrains_Mono']">
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}

export function SessionList({ sessions, activeSessionId, onSelect, onNew }) {
  return (
    <div className="space-y-1" data-testid="session-list">
      <Button onClick={onNew} className="w-full bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm text-xs font-bold mb-3" data-testid="new-chat-btn">
        <Plus className="w-3.5 h-3.5 mr-1.5" />New Chat
      </Button>
      {sessions.length === 0 && <p className="text-xs text-zinc-600 text-center py-4">No previous sessions</p>}
      {sessions.map((s) => (
        <button key={s.session_id} onClick={() => onSelect(s.session_id)} data-testid={`session-${s.session_id}`}
          className={`w-full text-left p-3 rounded-sm border transition-all ${activeSessionId === s.session_id ? 'border-[#FF5E1A]/40 bg-[#FF5E1A]/5' : 'border-[#27272A] bg-[#0A0A0A] hover:border-zinc-600'}`}>
          <p className="text-xs text-white truncate font-medium">{s.last_message || 'Empty session'}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-zinc-600 font-['JetBrains_Mono']">{s.message_count} msgs</span>
            {s.last_mode && s.last_mode !== 'general' && <Badge className="text-[8px] px-1 py-0 rounded-sm bg-zinc-800 text-zinc-500">{s.last_mode}</Badge>}
          </div>
        </button>
      ))}
    </div>
  );
}

export function ModeSelector({ mode, setMode }) {
  return (
    <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[#27272A] bg-[#050505] overflow-x-auto">
      {MODES.map((m) => {
        const Icon = m.icon;
        return (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-all ${mode === m.id ? `${m.bg} ${m.color} border border-current/20` : 'text-zinc-500 hover:text-zinc-300 border border-transparent'}`}>
            <Icon className="w-3.5 h-3.5" />{m.label}
          </button>
        );
      })}
    </div>
  );
}

export function WelcomeScreen({ vehicle, onQuickPrompt }) {
  return (
    <div className="flex flex-col items-center justify-center h-full" data-testid="ai-welcome">
      <div className="w-14 h-14 bg-[#FF5E1A]/10 border border-[#FF5E1A]/20 flex items-center justify-center rounded-sm mb-5">
        <Bot className="w-7 h-7 text-[#FF5E1A]" />
      </div>
      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-1">AI Tuner Pro Copilot</h3>
      <p className="text-sm text-zinc-500 mb-6 text-center max-w-md">
        Ask me anything about ECU tuning, fuel maps, datalogs, or binary analysis. I know your {vehicle?.ecu_type || 'ECU'} inside and out.
      </p>
      <p className="text-xs text-zinc-600 uppercase tracking-wider mb-3 font-bold">Quick Prompts</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
        {QUICK_PROMPTS.map((prompt) => (
          <button key={prompt} onClick={() => onQuickPrompt(prompt)} data-testid="quick-prompt"
            className="text-left p-3 bg-[#111111] border border-[#27272A] rounded-sm text-xs text-zinc-300 hover:border-[#FF5E1A]/30 hover:text-white transition-all">
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MessageList({ messages, sending, messagesEndRef }) {
  return (
    <div className="space-y-4">
      {messages.map((msg, i) => (
        <MessageBubble key={`${msg.role}-${msg.created_at || i}`} msg={msg} />
      ))}
      {sending && (
        <div className="flex gap-3" data-testid="ai-thinking">
          <div className="w-7 h-7 rounded-sm bg-[#FF5E1A]/20 flex items-center justify-center flex-shrink-0">
            <Bot className="w-3.5 h-3.5 text-[#FF5E1A]" />
          </div>
          <div className="bg-[#111111] border border-[#27272A] rounded-sm px-4 py-3">
            <div className="flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 text-[#FF5E1A] animate-spin" />
              <span className="text-xs text-zinc-500">Analyzing...</span>
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

export function ChatInput({ input, setInput, mode, sending, onSend, inputRef }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(input); }
  };

  return (
    <div className="border-t border-[#27272A] bg-[#0A0A0A] p-4">
      <div className="flex gap-2">
        <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
          placeholder={`Ask AI Tuner Pro (${MODES.find(m => m.id === mode)?.label} mode)...`}
          className="flex-1 bg-[#050505] border border-[#27272A] text-white text-sm rounded-sm px-4 py-3 resize-none focus:border-[#FF5E1A] outline-none font-['Inter'] placeholder:text-zinc-600 min-h-[44px] max-h-[120px]"
          rows={1} disabled={sending} data-testid="ai-input" />
        <Button onClick={() => onSend(input)} disabled={!input.trim() || sending}
          className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm px-4 self-end" data-testid="ai-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-[10px] text-zinc-600 mt-2 font-['JetBrains_Mono']">
        Shift+Enter for new line. AI responses are advisory — always verify with datalog feedback before flashing.
      </p>
    </div>
  );
}

export function ChatHeader({ vehicle, showSidebar, setShowSidebar }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A] bg-[#0A0A0A]">
      <div className="flex items-center gap-3">
        <button onClick={() => setShowSidebar(!showSidebar)} className="md:hidden text-zinc-400 hover:text-white" data-testid="toggle-sidebar-btn">
          {showSidebar ? <ChevronLeft className="w-5 h-5" /> : <History className="w-5 h-5" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#FF5E1A]/20 flex items-center justify-center rounded-sm"><Bot className="w-4 h-4 text-[#FF5E1A]" /></div>
          <div>
            <p className="text-sm font-bold text-white font-['Outfit']">AI Tuner Pro</p>
            <p className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">Claude Sonnet 4.5 — ECU Expert</p>
          </div>
        </div>
      </div>
      {vehicle && (
        <Badge className="bg-zinc-800 text-zinc-400 text-[10px] rounded-sm hidden sm:flex">
          {vehicle.year} {vehicle.make} {vehicle.model} — {vehicle.ecu_type}
        </Badge>
      )}
    </div>
  );
}
